import { z } from 'zod'
import { isAxiosError } from '@/utils/axiosHelper'

/**
 * The v2 endpoint's error body, observed in practice as `{ message }`
 * (e.g. the 403 lockout copy); the API notes describe `{ error }`. Accept
 * either rather than guessing wrong for one of the two.
 */
const pinV2ErrorBody = z
  .object({
    message: z.string().optional(),
    error: z.string().optional(),
  })
  .transform((body) => body.message ?? body.error)

/**
 * Reads the v2 PIN-login error body. The API already distinguishes a wrong
 * PIN from a locked-out account with different copy, so this just surfaces
 * it verbatim rather than the frontend inventing its own wording.
 *
 * `.unwrap()` on the mutation throws the raw `AxiosError` here (not RTK
 * Query's usual `{ status, data }` shape): `axiosBaseQuery`
 * (`morpheus.slice.ts`) calls `retry.fail(err)` with the original axios error
 * for any 4xx status, and RTK's retry wrapper re-throws that exact value on
 * `.unwrap()` rather than the `{ status, data }` object the same function
 * returns for a non-bailout error.
 */
export function readPinV2Error(error: unknown): string {
  if (
    isAxiosError(error) &&
    error.response?.status &&
    error.response.status >= 400 &&
    error.response.status < 500
  ) {
    const parsed = pinV2ErrorBody.safeParse(error.response.data)
    if (parsed.success && parsed.data) {
      return parsed.data
    }
  }
  return 'Unexpected error occurred during authentication'
}
